<template>
  <div  class="wave-card clickable"
        :class="{ 'is-completed': isCompleted, clickable: hasChildren }" >

    <div class="wave-card-container">
      <!-- Header: Wave Name + Status Badge -->
      <div class="wave-card-icon" :class="{ 'is-expanded': isExpanded }">
        <ChevronDown v-show="hasChildren" />
      </div>
      <div class="wave-info">
        <div class="wave-tab active">
          <div class="tab-label">
            {{ wave.Name || 'Wave Name' }}
          </div>
        </div>
      </div>

      <!-- Right Column: Status Badge -->
      <div class="wave-status">
        <div class="status-badge" :class="`status-${waveStatus}`">
          <div class="tab-label">{{ getStatusText() }}</div>
        </div>
      </div>

      <!-- Bottom Row: Stats Grid -->
      <div class="wave-stats">
        <StackedBarItem
                  :label="$t('Webportal.Dashboard.Total')"
                  :total="wave.Total"
                  :completed="wave.Completed"
                  :in-progress="wave.InProgress"
                  :revision="wave.WithRevision" />
        <BarItem  :label="$t('Webportal.Completed')"
                  :value="wave.Completed"
                  :total="wave.Total"
                  bar-color="green"
                  value-class="stat-completed" />
        <BarItem  :label="$t('Webportal.InProgress')"
                  :value="wave.InProgress"
                  :total="wave.Total"
                  bar-color="yellow"
                  value-class="stat-processing" />
        <BarItem  :label="$t('Webportal.Revisions')"
                  :value="wave.WithRevision"
                  :total="wave.Total"
                  bar-color="red"
                  value-class="stat-claimed" />
      </div>
    </div>
  </div>
</template>

<script>
import { Play, CircleCheckBig, ChevronDown } from 'lucide-vue-next';
import BarItem from './BarItem.vue';
import StackedBarItem from './StackedBarItem.vue';

export default {
  name: 'WaveCard',

  components: {
    Play,
    CircleCheckBig,
    ChevronDown,
    BarItem,
    StackedBarItem
  },

  props: {
    wave:       { type: Object,  required: true },
    isExpanded: { type: Boolean, default: false }
  },

  computed: {
    isCompleted() {
      return this.waveStatus === 'completed';
    },

    statusIcon() {
      return this.isCompleted ? CircleCheckBig : Play;
    },

    waveStatus() {
      return this.wave.WaveCompleted ? 'completed' : 'active';
    },

    hasChildren() {
      return this.wave?.Children?.length > 0;
    }
  },

  methods: {
    getStatusText() {
      const statusMap = {
        'active': this.$t('Webportal.Wave.Running'),
        'completed': this.$t('Webportal.Wave.Completed'),
        'planned': this.$t('Webportal.Wave.Planned')
      };
      return statusMap[this.waveStatus] || this.waveStatus;
    },

    getWaveDateText() {
      if (!this.wave) return '';
      const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
      const start = new Date(this.wave?.WaveDate).toLocaleDateString('de-DE', options);
      const end = new Date(this.wave?.WaveUntil).toLocaleDateString('de-DE', options);
      return `${start} - ${end}`;
    },
  }
}
</script>

<style lang="scss" scoped>
.wave-card-container {
  display: grid;
  grid-template-columns: auto 1fr 1fr;
  grid-template-rows: auto auto;
  // transform: skewX(var(--target-skew-angle-inverse));
}

.wave-card-icon {
  // transform: skewX(calc(1.85 * var(--target-skew-angle)));
  transition: transform 0.3s ease;

  svg {
    transition: transform 0.3s ease;
  }

  &.is-expanded svg {
    transform: rotate(180deg);
  }
}

.wave-card {
  background-color: var(--target-card-bg);
  border: 1px solid var(--bs-border-color);
  padding: 1rem 2.0rem;
  margin: 0.25rem;
  display: flex;
  flex-direction: column;
  // transform: skewX(var(--target-skew-angle-inverse));
  position: relative;
  border-radius: var(--bs-border-radius-sm);
}

.wave-tab {
  color: var(--color-foreground);
  border: 1px solid var(--color-border);
  padding: 4px 8px;
  border-radius: var(--bs-border-radius-sm);
  font-size: 12px;
  line-height: 16px;
  // color: var(--color-muted-foreground);
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  min-width: 25%;
  &:hover {
    background: var(--color-accent);
  }

  &.active {
    background: var(--target-card-header-bg);
    color: var(--color-foreground);
    border: 1px solid var(--color-border);
  }
}
.tab-label {
  // transform: skewX(calc(1 * var(--target-skew-angle)));
  width: 100%;
}

.wave-status {
  display: flex;
    font-size: 12px;
  align-items: flex-start;
  justify-content: flex-end;
}

.wave-info {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  // transform: skewX(calc(1 * var(--target-skew-angle)));
  flex: 1;
}

.status-badge {
  padding: 4px 8px;
  border-radius: var(--bs-border-radius-sm);
  // transform: skewX(calc(1 * var(--target-skew-angle)));

  span {
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    white-space: nowrap;
  }

  &.status-active {
    background: var(--default-status-bg-yellow);
    border: 1px solid var(--default-status-table-bg-yellow);
    color: var(--default-status-yellow);
    span {
      color: var(--default-status-yellow);
    }
  }

  &.status-completed {
    background: var(--default-status-bg-green);
    border: 1px solid var(--default-status-table-bg-green);
    color: var(--default-status-green);
    span {
      color: var(--default-status-green);
    }
  }

  &.status-planned {
    background: var(--default-status-muted-bg-indifferent);
    border: 1px solid var(--default-status-table-bg-indifferent);
    color: var(--default-status-indifferent);
    span {
      color: var(--default-status-indifferent);
    }
  }
}

.wave-stats {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  // transform: skewX(calc(1.875 * var(--target-skew-angle)));
  flex-wrap: wrap;
}
</style>
